import React, { useState, useEffect, useCallback, useRef } from 'react';
import Sortable from 'sortablejs';
import { Stethoscope, Plus, Edit2, RefreshCw, X, Check, GripVertical } from 'lucide-react';
import { specialityAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import useDialog from '../hooks/useDialog';
import useKeyboardReorder from '../hooks/useKeyboardReorder';

// Admin management of the doctor speciality catalog (GP, Gynaecology, Cardiology, ...).
// A doctor is linked to one speciality, and the prescription instruction chips plus the quick lab
// test lists can be scoped to a speciality so each discipline gets its own picker lists.
// Deactivating keeps the link on doctors who already use it, it just hides the entry from pickers.

// Shared grid template for the header + every row (grip · name · status · actions). A div grid, not
// a <table>, so the dragged clone transforms cleanly.
const ROW_GRID = 'grid grid-cols-[2.5rem_1fr_8rem_6rem] items-center';

const SpecialitiesManagement = () => {
  const showToast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {id?, name, isActive} | null
  const [showModal, setShowModal] = useState(false);
  const specialityDialogRef = useDialog(() => { setShowModal(false); setEditing(null); });
  // Drag-to-reorder via SortableJS. Refs let the once-created Sortable callback read the latest
  // items / toast without being re-bound on every render.
  const itemsRef = useRef(null);
  const showToastRef = useRef(showToast);
  const sortableRef = useRef(null);
  const saveOrderRef = useRef(null);
  itemsRef.current = items;
  showToastRef.current = showToast;
  saveOrderRef.current = (next) => {
    specialityAPI
      .reorder(next.map((i) => i.id))
      .then(() => window.dispatchEvent(new Event('clinic:specialities-changed')))
      .catch(async (err) => {
        showToastRef.current('error', err.message || 'Failed to save order');
        try { const fresh = await specialityAPI.list(true); setItems(Array.isArray(fresh) ? fresh : []); } catch { /* ignore */ }
      });
  };

  const rowsContainerRef = useCallback((node) => {
    if (sortableRef.current) { sortableRef.current.destroy(); sortableRef.current = null; }
    if (!node) return;
    sortableRef.current = Sortable.create(node, {
      animation: 180,
      easing: 'cubic-bezier(0.2, 0, 0, 1)',
      forceFallback: true,
      fallbackTolerance: 4,
      filter: 'button',
      preventOnFilter: false,
      ghostClass: 'si-ghost',
      chosenClass: 'si-chosen',
      dragClass: 'si-drag',
      onEnd: (evt) => {
        const { oldIndex, newIndex } = evt;
        if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
        const current = itemsRef.current || [];
        const next = [...current];
        const [moved] = next.splice(oldIndex, 1);
        next.splice(newIndex, 0, moved);
        setItems(next); // keep React in sync with the DOM move Sortable just made
        saveOrderRef.current(next);
      },
    });
  }, []);

  const handleProps = useKeyboardReorder({
    items: items || [],
    onMove: setItems,
    onCommit: (next) => saveOrderRef.current(next),
    getLabel: (it) => it.name,
  });

  const load = useCallback(async () => {
    setItems(null);
    try {
      const list = await specialityAPI.list(true);
      setItems(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load specialities');
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing({ name: '', isActive: true }); setShowModal(true); };
  const openEdit = (it) => { setEditing({ id: it.id, name: it.name, isActive: it.isActive }); setShowModal(true); };

  const handleSave = async () => {
    const e = editing;
    if (!e.name.trim()) {
      showToast('error', 'Speciality name is required');
      return;
    }
    try {
      if (e.id) await specialityAPI.update(e.id, { name: e.name.trim() });
      else await specialityAPI.create({ name: e.name.trim() });
      showToast('success', e.id ? 'Speciality updated' : 'Speciality added');
      setShowModal(false);
      setEditing(null);
      load();
      window.dispatchEvent(new Event('clinic:specialities-changed'));
    } catch (err) {
      showToast('error', err.message || 'Failed to save speciality');
    }
  };

  const handleToggleActive = async (it) => {
    try {
      if (it.isActive) {
        const ok = await confirm({
          title: 'Deactivate speciality?',
          message: `"${it.name}" will no longer be offered when assigning a doctor. Doctors already using it keep their link.`,
          confirmText: 'Deactivate',
          tone: 'danger',
        });
        if (!ok) return;
        await specialityAPI.remove(it.id);
        showToast('success', 'Speciality deactivated');
      } else {
        await specialityAPI.update(it.id, { isActive: true });
        showToast('success', 'Speciality reactivated');
      }
      load();
      window.dispatchEvent(new Event('clinic:specialities-changed'));
    } catch (err) {
      showToast('error', err.message || 'Action failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <Stethoscope className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Specialities</h1>
                <p className="text-gray-600">Doctor specialities used to group instruction chips and quick lab tests</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} className="p-3 border border-gray-300 rounded-xl hover:bg-gray-50" title="Refresh">
                <RefreshCw className="h-5 w-5 text-gray-600" />
              </button>
              <button onClick={openAdd} className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">
                <Plus className="h-4 w-4" /> Add Speciality
              </button>
            </div>
          </div>
        </div>

        {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg text-red-700">{error}</div>}

        {items === null ? (
          <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Stethoscope className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-500">No specialities yet</h3>
            <p className="text-gray-400 text-sm mt-1">Click "Add Speciality" to create one.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-2.5 bg-emerald-50/60 border-b border-emerald-100 text-xs text-emerald-700">
              <GripVertical className="h-4 w-4 flex-shrink-0" />
              Drag rows, or use the arrow keys on a row's handle, to set the order specialities appear in dropdowns.
            </div>
            <div className={`${ROW_GRID} bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider`}>
              <div className="px-2 py-3"><span className="sr-only">Reorder</span></div>
              <div className="px-6 py-3 text-left">Speciality</div>
              <div className="px-4 py-3 text-left">Status</div>
              <div className="px-4 py-3 text-right">Actions</div>
            </div>
            <div ref={rowsContainerRef}>
              {items.map((it, index) => (
                <div
                  key={it.id}
                  className={`si-row ${ROW_GRID} border-b border-gray-100 last:border-b-0 bg-white hover:bg-gray-50/60 cursor-grab ${!it.isActive ? 'opacity-60' : ''}`}
                >
                  <div className="px-2 py-3 flex justify-center">
                    <span {...handleProps(it, index)} className="inline-flex p-1 rounded text-gray-400 hover:text-gray-600 cursor-grab focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"><GripVertical className="h-4 w-4" /></span>
                  </div>
                  <div className="px-6 py-3 text-sm font-medium text-gray-900">{it.name}</div>
                  <div className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        role="switch"
                        aria-checked={it.isActive}
                        onClick={() => handleToggleActive(it)}
                        title={it.isActive ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${it.isActive ? 'bg-emerald-500' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${it.isActive ? 'translate-x-4' : 'translate-x-0.5'}`} />
                      </button>
                      <span className={`text-xs font-medium ${it.isActive ? 'text-emerald-700' : 'text-gray-400'}`}>
                        {it.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      <button onClick={() => openEdit(it)} className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Edit">
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {showModal && editing && (
        <div ref={specialityDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) { setShowModal(false); setEditing(null); } }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{editing.id ? 'Edit Speciality' : 'Add Speciality'}</h3>
              <button aria-label="Close" onClick={() => { setShowModal(false); setEditing(null); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                  placeholder="e.g. Gynaecology"
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                />
                {editing.id && (
                  <p className="text-xs text-gray-500 mt-1.5">Renaming updates the speciality shown for every doctor linked to it.</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button onClick={() => { setShowModal(false); setEditing(null); }} className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 flex items-center gap-2">
                <Check className="h-4 w-4" /> {editing.id ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecialitiesManagement;
